/* Inline expandable card quick options — variant list that adds directly to cart. */

if (!customElements.get('card-quick-options')) {
  class CardQuickOptions extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('.card-quick-options__trigger');
      this.panel = this.querySelector('.card-quick-options__panel');
      this.form = this.querySelector('[data-card-quick-options-form]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.errorEl = this.querySelector('[data-card-quick-options-error]');
      this.cardWrapper =
        this.closest('.card-wrapper') ||
        this.closest('.product-card-minimal') ||
        this.closest('.complementary-product-card');
      this.isOpen = false;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.trigger || !this.panel || !this.form) return;
      this.trigger.addEventListener('click', this.toggle.bind(this));
      this.form.addEventListener('click', this.onOptionClick.bind(this));
      this.relocatePanel();
    }

    /**
     * Move the panel out of the small "+" cell and into the card image
     * container so it can slide up from the bottom of the image instead
     * of from the bottom of the entire card-wrapper.
     */
    relocatePanel() {
      const panelTarget =
        this.cardWrapper?.querySelector('.card__inner') ||
        this.cardWrapper?.querySelector('.product-card-minimal__media') ||
        this.cardWrapper?.querySelector('.complementary-product-card__media');

      if (panelTarget && this.panel.parentElement !== panelTarget) {
        panelTarget.appendChild(this.panel);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    toggle(event) {
      if (event) event.preventDefault();
      if (this.isOpen) this.close();
      else this.open();
    }

    open() {
      if (this.isOpen) return;
      this.closeOthers();
      this.isOpen = true;
      this.panel.hidden = false;
      requestAnimationFrame(() => {
        this.panel.setAttribute('data-open', 'true');
      });
      this.trigger.setAttribute('aria-expanded', 'true');
      if (this.cardWrapper) {
        this.cardWrapper.classList.add('card-wrapper--quick-options-open');
        this.cardWrapper.classList.add('product-card-minimal--quick-options-open');
        this.cardWrapper.classList.add('complementary-product-card--quick-options-open');
      }
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.cardWrapper) {
        this.cardWrapper.classList.remove('card-wrapper--quick-options-open');
        this.cardWrapper.classList.remove('product-card-minimal--quick-options-open');
        this.cardWrapper.classList.remove('complementary-product-card--quick-options-open');
      }
      this.clearError();
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      window.setTimeout(() => {
        if (!this.isOpen) this.panel.hidden = true;
      }, 320);
    }

    closeOthers() {
      document.querySelectorAll('card-quick-options').forEach((node) => {
        if (node !== this && typeof node.close === 'function') node.close();
      });
    }

    onDocumentClick(event) {
      if (!this.isOpen) return;
      if (this.contains(event.target) || this.panel.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
        this.trigger.focus();
      }
    }

    onOptionClick(event) {
      const button = event.target.closest('.card-quick-options__option');
      if (!button) return;
      event.preventDefault();
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.submitVariant(variantId, button);
    }

    submitVariant(variantId, button) {
      this.clearError();
      this.variantInput.value = variantId;
      this.trigger.classList.add('loading');
      button.classList.add('loading');
      const spinner = this.trigger.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const cartTarget = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionsToBundle = [];
      if (cartTarget && typeof cartTarget.getSectionsToRender === 'function') {
        cartTarget.getSectionsToRender().forEach((section) => {
          const sectionKey = section.section || section.id;
          if (sectionKey) sectionsToBundle.push(sectionKey);
        });
      }

      const formData = new FormData(this.form);
      if (sectionsToBundle.length) {
        formData.append('sections', sectionsToBundle.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      if (cartTarget && typeof cartTarget.setActiveElement === 'function') {
        cartTarget.setActiveElement(this.trigger);
      }

      const config = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      };

      const cartAddUrl =
        (window.routes && window.routes.cart_add_url) ||
        (typeof routes !== 'undefined' && routes.cart_add_url) ||
        '/cart/add';

      fetch(`${cartAddUrl}.js`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message || response.errors || 'Error');
            return;
          }
          if (cartTarget && typeof cartTarget.renderContents === 'function') {
            cartTarget.renderContents(response);
          } else {
            this.refreshCartCount();
          }
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'card-quick-options',
                productVariantId: variantId,
                cartData: response,
              });
            }
          } catch (_) {
            /* publish not available — cart target re-render is enough */
          }
          this.close();
        })
        .catch(() => {
          const message =
            (window.accessibilityStrings && window.accessibilityStrings.networkError) ||
            (window.accessibilityStrings && window.accessibilityStrings.error);
          this.showError(message);
        })
        .finally(() => {
          this.trigger.classList.remove('loading');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    refreshCartCount() {
      const cartUrl =
        (window.routes && window.routes.cart_url) ||
        (typeof routes !== 'undefined' && routes.cart_url) ||
        '/cart';
      fetch(`${cartUrl}.js`)
        .then((response) => response.json())
        .then((cart) => {
          document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
            const counter = bubble.querySelector('span[aria-hidden="true"]');
            if (counter) counter.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent =
        typeof message === 'string'
          ? message
          : (window.accessibilityStrings && window.accessibilityStrings.error) || '';
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  customElements.define('card-quick-options', CardQuickOptions);
}
